// Shared semantic N-gram computation
// Extracted from App.jsx

// ─── Shared: compute semantic N-grams from clusters ───
const STOP_WORDS_CLIENT = new Set([
  "та","і","в","у","на","з","із","за","до","від","по","для","як","що","це","той","ця","ті","але","бо",
  "ж","же","ні","не","так","чи","або","ще","вже","їх","його","її","він","вона","воно","вони","ми","ви",
  "при","про","через","між","під","над","без","щоб","коли","де","які","яка","який","яке","яких",
  "було","буде","був","була","були","є","бути","може","можна","треба","потрібно","також",
  "після","перед","свої","свій","все","всі","весь","кожен","кожна","лише","тільки",
  "и","в","на","с","по","за","из","от","до","для","не","но","да","то","же","бы","ли","как","что",
  "это","его","их","он","она","оно","они","мы","вы","все","при","про","без","под","над","между",
  "через","после","перед","если","был","была","было","были","быть","будет","может","можно",
  "the","a","an","is","are","was","were","be","been","being","have","has","had","do","does","did",
  "will","would","shall","should","may","might","can","could","must","to","of","in","for",
  "on","with","at","by","from","as","into","through","during","before","after","and","but","or","if",
]);

export const computeSemNgrams = (clusters) => {
  if (!clusters.length) return null;

  const tokenize = (text) => {
    const words = text.toLowerCase().match(/[a-zа-яёіїєґ]+(?:['-][a-zа-яёіїєґ]+)*/gi) || [];
    return words.filter(w => !STOP_WORDS_CLIENT.has(w) && w.length >= 3);
  };

  const extractNgrams = (tokens, n) => {
    const grams = [];
    for (let i = 0; i <= tokens.length - n; i++) {
      const parts = tokens.slice(i, i + n);
      let hasDup = false;
      for (let j = 1; j < parts.length; j++) { if (parts[j] === parts[j - 1]) { hasDup = true; break; } }
      if (!hasDup) grams.push(parts.join(" "));
    }
    return grams;
  };

  const byUrlMap = new Map();
  let totalKw = 0;
  for (const c of clusters) {
    const url = c.url || "unknown";
    const kws = (c.keywords || []).map(k => {
      if (typeof k === "string") return { keyword: k, frequency: 1 };
      return { keyword: k.keyword || k.name || "", frequency: k.frequency || k.freq || 1 };
    }).filter(k => k.keyword);
    if (!kws.length) continue;
    if (!byUrlMap.has(url)) byUrlMap.set(url, []);
    const existing = new Set(byUrlMap.get(url).map(k => k.keyword.toLowerCase()));
    for (const kw of kws) {
      const lower = kw.keyword.toLowerCase();
      if (!existing.has(lower)) {
        existing.add(lower);
        byUrlMap.get(url).push(kw);
        totalKw++;
      }
    }
  }

  if (!totalKw) return null;

  const buildPageNgrams = (keywords) => {
    const gramData = { 1: new Map(), 2: new Map(), 3: new Map() };
    for (const kw of keywords) {
      const tokens = tokenize(kw.keyword);
      for (const n of [1, 2, 3]) {
        const grams = extractNgrams(tokens, n);
        const seen = new Set();
        for (const gram of grams) {
          if (seen.has(gram)) continue;
          seen.add(gram);
          if (!gramData[n].has(gram)) gramData[n].set(gram, { weight: 0, count: 0, sources: [] });
          const entry = gramData[n].get(gram);
          entry.weight += kw.frequency;
          entry.count++;
          entry.sources.push({ keyword: kw.keyword, frequency: kw.frequency });
        }
      }
    }
    return gramData;
  };

  const assignPriorities = (items) => {
    if (!items.length) return items;
    const sorted = [...items].sort((a, b) => b.weight - a.weight);
    const top20idx = Math.max(1, Math.ceil(sorted.length * 0.2));
    const top50idx = Math.max(1, Math.ceil(sorted.length * 0.5));
    const top20threshold = sorted[top20idx - 1]?.weight || 0;
    const top50threshold = sorted[top50idx - 1]?.weight || 0;
    return items.map(item => ({
      ...item,
      priority: (item.weight >= top20threshold || item.count >= 3) ? "high" : item.weight >= top50threshold ? "medium" : "low",
    }));
  };

  const toArray = (gramMap, limit = 500) =>
    Array.from(gramMap.entries())
      .map(([gram, d]) => ({ gram, weight: d.weight, count: d.count, sources: d.sources }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, limit);

  const byPage = new Map();
  for (const [url, keywords] of byUrlMap) {
    const gramData = buildPageNgrams(keywords);
    byPage.set(url, {
      unigrams: assignPriorities(toArray(gramData[1])),
      bigrams: assignPriorities(toArray(gramData[2])),
      trigrams: assignPriorities(toArray(gramData[3])),
    });
  }

  const globalGrams = { 1: new Map(), 2: new Map(), 3: new Map() };
  for (const [url, keywords] of byUrlMap) {
    for (const kw of keywords) {
      const tokens = tokenize(kw.keyword);
      for (const n of [1, 2, 3]) {
        const grams = extractNgrams(tokens, n);
        const seen = new Set();
        for (const gram of grams) {
          if (seen.has(gram)) continue;
          seen.add(gram);
          if (!globalGrams[n].has(gram)) globalGrams[n].set(gram, { weight: 0, count: 0, pages: new Set(), sources: [] });
          const entry = globalGrams[n].get(gram);
          entry.weight += kw.frequency;
          entry.count++;
          entry.pages.add(url);
          entry.sources.push({ keyword: kw.keyword, frequency: kw.frequency, url });
        }
      }
    }
  }

  const globalToArray = (gramMap, limit = 500) =>
    Array.from(gramMap.entries())
      .map(([gram, d]) => ({ gram, weight: d.weight, count: d.count, pages: d.pages.size, sources: d.sources }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, limit);

  return {
    global: {
      unigrams: globalToArray(globalGrams[1]),
      bigrams: globalToArray(globalGrams[2]),
      trigrams: globalToArray(globalGrams[3]),
    },
    byPage,
    totalKeywords: totalKw,
    totalPages: byUrlMap.size,
    pageUrls: [...byUrlMap.keys()],
  };
};

// ═══ NGRAM ANALYSIS VIEW ═══
